// --------------------------------------------------------------
// Created On: 2026-11-02
// Author: Zachary Thomas
//
// Last Modified: 2026-11-02
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useId } from "react";
import styles from "./SystemControlComponentGaugeRadial.module.scss";

// Radial graphic that fills along a half circle from left to right, with its value, minimum, and maximum overlaid.
export default function SystemControlComponentGaugeRadial(props: Props): Component {
  // Colons in generated IDs are not valid inside of the url() reference of a mask.
  const maskId = `radial-mask-${useId().replace(/:/g, "")}`;

  // Returns the custom properties that drive the fill level and the highlight color of the graphic.
  function getRadialStyle(): React.CSSProperties {
    const radialStyle: { [key: string]: string | number } = { "--gauge-fill-percent": props.fillPercent };
    if (props.highlightColor !== null) {
      radialStyle["--gauge-fill-color"] = props.highlightColor;
      radialStyle["--gauge-tint-color"] = props.highlightColor;
    }
    return radialStyle as React.CSSProperties;
  }

  // Returns the class names for the graphic.
  function getGraphicClassName(): string {
    if (props.highlightColor !== null) {
      return `${styles.graphic} ${styles.highlighted}`;
    } else {
      return styles.graphic;
    }
  }

  return (
    <div className={styles.radial} style={getRadialStyle()}>
      <div className={getGraphicClassName()}>
        <svg className={styles.svg} viewBox="0 0 229 120" xmlns="http://www.w3.org/2000/svg">
          <path
            className={styles.background}
            d="M185.851 100.285C187.29 107.766 193.348 114 200.99 114C208.633 114 214.929 107.79 213.887 100.243C207.162 51.5244 165.228 14 114.502 14C63.775 14 21.8414 51.5244 15.1164 100.243C14.0747 107.79 20.3704 114 28.013 114C35.6557 114 41.7136 107.766 43.1526 100.285C49.5833 66.8499 79.0825 41.5862 114.502 41.5862C149.921 41.5862 179.42 66.8499 185.851 100.285Z"
          />
          <path
            className={styles.tint}
            d="M185.851 100.285C187.29 107.766 193.348 114 200.99 114C208.633 114 214.929 107.79 213.887 100.243C207.162 51.5244 165.228 14 114.502 14C63.775 14 21.8414 51.5244 15.1164 100.243C14.0747 107.79 20.3704 114 28.013 114C35.6557 114 41.7136 107.766 43.1526 100.285C49.5833 66.8499 79.0825 41.5862 114.502 41.5862C149.921 41.5862 179.42 66.8499 185.851 100.285Z"
          />
          <mask
            id={maskId}
            style={{ maskType: "alpha" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="229"
            height="120"
          >
            <path
              d="M185.851 100.285C187.29 107.766 193.348 114 200.99 114C208.633 114 214.929 107.79 213.887 100.243C207.162 51.5244 165.228 14 114.502 14C63.775 14 21.8414 51.5244 15.1164 100.243C14.0747 107.79 20.3704 114 28.013 114C35.6557 114 41.7136 107.766 43.1526 100.285C49.5833 66.8499 79.0825 41.5862 114.502 41.5862C149.921 41.5862 179.42 66.8499 185.851 100.285Z"
              fill="black"
            />
          </mask>
          <g mask={`url(#${maskId})`}>
            <path
              className={styles.fill}
              d="M215 114C215 58.7715 170.005 14 114.5 14C58.9954 14 14 58.7715 14 114H215Z"
            />
          </g>
          <path
            className={styles.outline}
            fillRule="evenodd"
            clipRule="evenodd"
            d="M212.893 100.38C206.235 52.1489 164.72 15 114.502 15C64.2831 15 22.7679 52.1489 16.1103 100.38C15.1643 107.233 20.8814 113 28.0131 113C35.0645 113 40.7965 107.223 42.1672 100.096C48.6869 66.1994 78.5922 40.5862 114.502 40.5862C150.411 40.5862 180.316 66.1994 186.836 100.096C188.207 107.223 193.939 113 200.99 113C208.122 113 213.839 107.233 212.893 100.38ZM200.99 114C193.348 114 187.29 107.766 185.851 100.285C179.42 66.8499 149.921 41.5862 114.502 41.5862C79.0825 41.5862 49.5833 66.8499 43.1526 100.285C41.7136 107.766 35.6557 114 28.0131 114C20.3704 114 14.0747 107.79 15.1164 100.243C21.8414 51.5244 63.775 14 114.502 14C165.228 14 207.162 51.5244 213.887 100.243C214.929 107.79 208.633 114 200.99 114Z"
          />
        </svg>
        <div className={styles.overlay}>
          <div data-test="system-control-gauge-value" className={styles.value}>
            {props.value === null && "N/A"}
            {props.value !== null && props.value}
            {props.value !== null && props.unitShortName !== null && (
              <span className={styles.units}>{props.unitShortName}</span>
            )}
          </div>
        </div>
      </div>
      <div className={styles.scale}>
        <span data-test="system-control-gauge-min">{props.min}</span>
        <span data-test="system-control-gauge-max">{props.max}</span>
      </div>
    </div>
  );
}

interface Props {
  value: string | null;
  unitShortName: string | null;
  fillPercent: number;
  highlightColor: string | null;
  min: number | string;
  max: number | string;
}
